import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Save as SaveIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../contexts/AuthContext';
import { CourierZone, NeighborhoodSuggestion, courierModuleService } from './courierModuleService';

interface Props {
  open: boolean;
  onClose: () => void;
  onChanged?: () => void;
}

interface DraftZone {
  key: string;
  id?: number;
  name: string;
  neighborhoods: string[];
  sortOrder: number;
  dirty: boolean;
}

const toDraft = (z: CourierZone): DraftZone => ({
  key: `z${z.id}`,
  id: z.id,
  name: z.name,
  neighborhoods: z.neighborhoods,
  sortOrder: z.sortOrder,
  dirty: false,
});

const ZonesDialog: React.FC<Props> = ({ open, onClose, onChanged }) => {
  const { t } = useTranslation('salesSystem');
  const { branches } = useAuth();
  const [branchId, setBranchId] = useState('');
  const [zones, setZones] = useState<DraftZone[]>([]);
  const [suggestions, setSuggestions] = useState<NeighborhoodSuggestion[]>([]);
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const [inputs, setInputs] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && !branchId && branches.length > 0) setBranchId(branches[0].id);
  }, [open, branches, branchId]);

  const load = useCallback(async () => {
    if (!branchId) return;
    setLoading(true);
    setError(null);
    try {
      const [list, sugg] = await Promise.all([
        courierModuleService.listZones(branchId),
        courierModuleService.suggestions(branchId),
      ]);
      const drafts = list.map(toDraft);
      setZones(drafts);
      setSuggestions(sugg);
      setActiveKey((prev) => (prev && drafts.some((d) => d.key === prev) ? prev : drafts[0]?.key ?? null));
    } catch (e: any) {
      setError(e?.message);
    } finally {
      setLoading(false);
    }
  }, [branchId]);

  useEffect(() => {
    if (open) void load();
  }, [open, load]);

  const update = (key: string, patch: Partial<DraftZone>) =>
    setZones((prev) => prev.map((z) => (z.key === key ? { ...z, ...patch, dirty: true } : z)));

  const addNeighborhood = (key: string, name: string) => {
    const value = name.trim();
    if (!value) return;
    const zone = zones.find((z) => z.key === key);
    if (!zone || zone.neighborhoods.some((n) => n.toLocaleLowerCase('tr') === value.toLocaleLowerCase('tr'))) return;
    update(key, { neighborhoods: [...zone.neighborhoods, value] });
  };

  const saveZone = async (zone: DraftZone, override?: Partial<DraftZone>) => {
    const z = { ...zone, ...override };
    setSavingKey(z.key);
    setError(null);
    try {
      const saved = await courierModuleService.saveZone({
        id: z.id ?? null,
        branchId,
        name: z.name,
        neighborhoods: z.neighborhoods,
        sortOrder: z.sortOrder,
      });
      setZones((prev) => prev.map((p) => (p.key === z.key ? { ...toDraft(saved), key: z.key } : p)));
      setSuggestions(await courierModuleService.suggestions(branchId));
      onChanged?.();
    } catch (e: any) {
      setError(e?.message);
    } finally {
      setSavingKey(null);
    }
  };

  const removeZone = async (zone: DraftZone) => {
    if (zone.id) {
      try {
        await courierModuleService.deleteZone(zone.id);
        onChanged?.();
      } catch (e: any) {
        setError(e?.message);
        return;
      }
    }
    setZones((prev) => prev.filter((z) => z.key !== zone.key));
    if (zone.id) setSuggestions(await courierModuleService.suggestions(branchId).catch(() => suggestions));
  };

  const addZone = () => {
    const key = `new${Date.now()}`;
    setZones((prev) => [
      ...prev,
      { key, name: '', neighborhoods: [], sortOrder: prev.length, dirty: true },
    ]);
    setActiveKey(key);
  };

  // Öneriye tıklanınca seçili bölgeye eklenir ve hemen kaydedilir
  const takeSuggestion = (s: NeighborhoodSuggestion) => {
    const zone = zones.find((z) => z.key === activeKey);
    if (!zone) return;
    if (!zone.name.trim()) {
      addNeighborhood(zone.key, s.name);
      return;
    }
    void saveZone(zone, { neighborhoods: [...zone.neighborhoods, s.name] });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1 }}>{t('courierModule.zones.title')}</Box>
        <TextField select size="small" label={t('courierModule.zones.branch')} value={branchId}
          onChange={(e) => setBranchId(e.target.value)} sx={{ minWidth: 220 }}>
          {branches.map((b) => (
            <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>
          ))}
        </TextField>
        <Button variant="contained" startIcon={<AddIcon />} onClick={addZone} disabled={!branchId}>
          {t('courierModule.zones.add')}
        </Button>
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {loading ? (
          <Box sx={{ display: 'grid', placeItems: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) 300px' }, gap: 2 }}>
            <Stack spacing={1.5}>
              {zones.length === 0 && <Alert severity="info">{t('courierModule.zones.empty')}</Alert>}
              {zones.map((z) => (
                <Paper
                  key={z.key}
                  variant="outlined"
                  onClick={() => setActiveKey(z.key)}
                  sx={{
                    p: 1.5,
                    cursor: 'pointer',
                    borderWidth: activeKey === z.key ? 2 : 1,
                    borderColor: activeKey === z.key ? 'primary.main' : 'divider',
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <TextField size="small" label={t('courierModule.zones.name')} value={z.name}
                      onChange={(e) => update(z.key, { name: e.target.value })} sx={{ flex: 1 }} />
                    <Tooltip title={t('courierModule.actions.save')}>
                      <span>
                        <IconButton color="primary" disabled={!z.dirty || savingKey === z.key || !z.name.trim()}
                          onClick={(e) => { e.stopPropagation(); void saveZone(z); }}>
                          {savingKey === z.key ? <CircularProgress size={18} /> : <SaveIcon />}
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={t('courierModule.zones.delete')}>
                      <IconButton onClick={(e) => { e.stopPropagation(); void removeZone(z); }}>
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1.25 }}>
                    {z.neighborhoods.map((n) => (
                      <Chip key={n} size="small" label={n}
                        onDelete={() => update(z.key, { neighborhoods: z.neighborhoods.filter((x) => x !== n) })} />
                    ))}
                    <TextField
                      size="small"
                      variant="standard"
                      placeholder={t('courierModule.zones.addNeighborhood')}
                      value={inputs[z.key] || ''}
                      onChange={(e) => setInputs((p) => ({ ...p, [z.key]: e.target.value }))}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addNeighborhood(z.key, inputs[z.key] || '');
                          setInputs((p) => ({ ...p, [z.key]: '' }));
                        }
                      }}
                      sx={{ minWidth: 180 }}
                    />
                  </Box>
                  {z.dirty && (
                    <Typography variant="caption" color="warning.main">
                      {t('courierModule.zones.unsaved')}
                    </Typography>
                  )}
                </Paper>
              ))}
            </Stack>

            <Paper variant="outlined" sx={{ p: 1.5, alignSelf: 'start' }}>
              <Typography variant="overline" color="text.secondary">
                {t('courierModule.zones.suggestions')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {t('courierModule.zones.suggestionsHelp')}
              </Typography>
              {suggestions.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {t('courierModule.zones.noSuggestions')}
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, maxHeight: 420, overflowY: 'auto' }}>
                  {suggestions.map((s) => (
                    <Chip
                      key={s.name}
                      size="small"
                      variant="outlined"
                      disabled={!activeKey}
                      label={`${s.name} · ${s.orderCount}`}
                      onClick={() => takeSuggestion(s)}
                    />
                  ))}
                </Box>
              )}
              <Alert severity="info" sx={{ mt: 1.5 }}>
                {t('courierModule.zones.matchNote')}
              </Alert>
            </Paper>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('courierModule.actions.close')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ZonesDialog;
